import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Flex, IconButton, Skeleton, Text } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import {
  getActiveTabIndex,
  getBlockMetadata,
  setActiveTabIndexVersion2,
} from 'redux/PlanPageSlice';
import { getCompareScenario, getVarianceFilterChart } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getDataTypeBasedValuewithNewValue } from 'utils/FormatValues';

import {
  selectCompareDefaultTabIndex,
  selectCompareIndicatorRows,
  selectCompareSafeActiveIndex,
  selectDefaultTabIndex,
  selectIsCompareMode,
  selectIsKpiLoading,
  selectPlanIndicatorRows,
  selectSafeActiveIndex,
} from '../planPageRevampSelectors';
import {
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_KPI_SELECTED_BG,
  PLAN_TEXT,
} from '../planPageRevampUi';

interface KpiEntry {
  name: string;
  value: string;
  subtitle: string | null;
  compareDelta?: number | null;
}

const removeSign = (value: number) => (value < 0 ? -1 * value : value);

const toTotalValuesArray = (totalValues: unknown): any[] => {
  if (!totalValues) return [];
  if (Array.isArray(totalValues)) return totalValues;
  return Object.values(totalValues as Record<string, unknown>);
};

const toPlanKpiEntry = (row: any, index: number, metadata: any): KpiEntry => {
  const indicatorMeta = metadata?.indicators?.[index];
  const dataFormat =
    row?.data_format || row?.type || indicatorMeta?.data_format || indicatorMeta?.format;
  const formatProperties = row?.format_properties || indicatorMeta?.format_properties;
  const totalValues = toTotalValuesArray(row?.total_values);

  let formattedValue = '-';
  if (row?.total != null) {
    formattedValue = String(
      getDataTypeBasedValuewithNewValue(dataFormat, row.total, formatProperties),
    );
  } else if (totalValues.length > 0) {
    const yearlyValues = totalValues.filter((item: any) => String(item?.name ?? '').length === 4);
    const latestValue =
      yearlyValues.length > 0
        ? yearlyValues[yearlyValues.length - 1]
        : totalValues[totalValues.length - 1];
    if (latestValue?.value != null) {
      formattedValue = String(
        getDataTypeBasedValuewithNewValue(dataFormat, latestValue.value, formatProperties),
      );
    }
  }

  const yearlyValues = totalValues.filter((item: any) => String(item?.name ?? '').length === 4);
  const latestYear = yearlyValues.length > 0 ? yearlyValues[yearlyValues.length - 1] : null;

  return {
    name: row?.name ?? indicatorMeta?.name ?? '',
    value: formattedValue,
    subtitle: latestYear ? `FY${latestYear.name}` : null,
  };
};

const toCompareKpiEntry = (row: any, varianceFilter: boolean): KpiEntry => {
  let formattedValue = '-';
  if (row?.total != null) {
    formattedValue = String(
      getDataTypeBasedValuewithNewValue(row.type, row.total, row.format_properties),
    );
  }

  const varianceValue = varianceFilter ? Number(row?.varience) : Number(row?.diff);
  let subtitle: string | null = null;
  if (!Number.isNaN(varianceValue) && varianceValue !== 0) {
    subtitle = varianceFilter
      ? `${removeSign(varianceValue).toFixed(1)}% vs compare`
      : `${removeSign(varianceValue).toFixed(1)} vs compare`;
  }

  return {
    name: row?.name ?? '',
    value: formattedValue,
    subtitle,
    compareDelta: Number.isNaN(varianceValue) ? null : varianceValue,
  };
};

interface KpiMetricCardProperties {
  entry: KpiEntry;
  index: number;
  isSelected: boolean;
  isCompareMode: boolean;
  primaryColor: string;
  textDark: string;
  textSecondary: string;
  successColor: string;
  dangerColor: string;
  activeBg: string;
  onSelect: (index: number) => void;
}

const KpiMetricCard = memo(
  ({
    entry,
    index,
    isSelected,
    isCompareMode,
    primaryColor,
    textDark,
    textSecondary,
    successColor,
    dangerColor,
    activeBg,
    onSelect,
  }: KpiMetricCardProperties) => {
    const handleClick = useCallback(() => {
      onSelect(index);
    }, [index, onSelect]);

    const subtitleColor = useMemo(() => {
      if (!entry.subtitle) return successColor;
      if (!isCompareMode || entry.compareDelta == null) return successColor;
      return entry.compareDelta > 0 ? successColor : dangerColor;
    }, [dangerColor, entry.compareDelta, entry.subtitle, isCompareMode, successColor]);

    if (isSelected) {
      return (
        <Box
          as='button'
          type='button'
          onClick={handleClick}
          flexShrink={0}
          minW='200px'
          px={5}
          pt={4}
          pb={3}
          display='flex'
          flexDirection='column'
          alignItems='flex-start'
          gap={1}
          border='1px solid'
          borderColor={PLAN_KPI_SELECTED_BG}
          borderBottom='none'
          borderTopRadius='12px'
          bg={PLAN_KPI_SELECTED_BG}
          position='relative'
          zIndex={2}
          mb='-1px'
          cursor='pointer'
          textAlign='left'
          _focus={{ outline: 'none', boxShadow: 'none' }}
        >
          <Text {...PLAN_TEXT.kpiFeaturedLabel} color={textDark} whiteSpace='nowrap' w='100%'>
            {entry.name}
          </Text>
          <Text {...PLAN_TEXT.kpiFeaturedValue} color={primaryColor} whiteSpace='nowrap' w='100%'>
            {entry.value}
          </Text>
          {entry.subtitle && (
            <Text
              {...PLAN_TEXT.kpiFeaturedSubtitle}
              color={subtitleColor}
              whiteSpace='nowrap'
              w='100%'
            >
              {entry.subtitle}
            </Text>
          )}
        </Box>
      );
    }

    return (
      <Box
        as='button'
        type='button'
        onClick={handleClick}
        flexShrink={0}
        minW='170px'
        px={5}
        pt={4}
        pb={3}
        display='flex'
        flexDirection='column'
        alignItems='flex-start'
        gap={1}
        border='none'
        borderTopRadius='12px'
        bg='transparent'
        cursor='pointer'
        textAlign='left'
        transition='background 0.15s ease'
        _hover={{ bg: activeBg }}
        _focus={{ outline: 'none', boxShadow: 'none' }}
      >
        <Text {...PLAN_TEXT.kpiLabel} color={textSecondary} whiteSpace='nowrap' w='100%'>
          {entry.name}
        </Text>
        <Text {...PLAN_TEXT.kpiValue} color={textDark} whiteSpace='nowrap' w='100%'>
          {entry.value}
        </Text>
        {isCompareMode && entry.subtitle && (
          <Text
            {...PLAN_TEXT.kpiFeaturedSubtitle}
            color={subtitleColor}
            whiteSpace='nowrap'
            w='100%'
          >
            {entry.subtitle}
          </Text>
        )}
      </Box>
    );
  },
);

KpiMetricCard.displayName = 'KpiMetricCard';

interface KpiSummaryBarSkeletonProperties {
  count: number;
  borderColor: string;
  surfaceColor: string;
}

const KpiSummaryBarSkeleton = memo(
  ({ count, borderColor, surfaceColor }: KpiSummaryBarSkeletonProperties) => (
    <Box position='relative' px={PLAN_HEADER_HORIZONTAL_PADDING} bg={surfaceColor}>
      <Flex alignItems='flex-end' gap={0} overflowX='hidden' pb={0} maxWidth='calc(100% - 50px)'>
        {Array.from({ length: count }, (_, index) => {
          const isFeatured = index === 0;

          return (
            <Box
              key={index}
              flexShrink={0}
              minW={isFeatured ? '200px' : '170px'}
              px={5}
              pt={4}
              pb={3}
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              gap={2}
              borderTopRadius='12px'
              bg={isFeatured ? PLAN_KPI_SELECTED_BG : 'transparent'}
            >
              <Skeleton
                height='14px'
                width={isFeatured ? '88px' : '72px'}
                borderRadius='4px'
                startColor='gray.100'
                endColor='gray.200'
              />
              <Skeleton
                height={isFeatured ? '24px' : '18px'}
                width={isFeatured ? '128px' : '96px'}
                borderRadius='4px'
                startColor='gray.100'
                endColor='gray.200'
              />
              {isFeatured && (
                <Skeleton
                  height='12px'
                  width='64px'
                  borderRadius='4px'
                  startColor='gray.100'
                  endColor='gray.200'
                />
              )}
            </Box>
          );
        })}
      </Flex>
      <Box w='100%' h='1px' bg={borderColor} />
    </Box>
  ),
);

KpiSummaryBarSkeleton.displayName = 'KpiSummaryBarSkeleton';

const KpiSummaryBar = () => {
  const dispatch: AppDispatch = useDispatch();
  const isCompareMode = useSelector(selectIsCompareMode);
  const planIndicatorRows = useSelector(selectPlanIndicatorRows);
  const compareIndicatorRows = useSelector(selectCompareIndicatorRows);
  const metadata = useSelector(getBlockMetadata);
  const activeTabIndex = useSelector(getActiveTabIndex);
  const planDefaultTabIndex = useSelector(selectDefaultTabIndex);
  const compareDefaultTabIndex = useSelector(selectCompareDefaultTabIndex);
  const planSafeActiveIndex = useSelector(selectSafeActiveIndex);
  const compareSafeActiveIndex = useSelector(selectCompareSafeActiveIndex);
  const varianceFilter = useSelector(getVarianceFilterChart);
  const compareScenario = useSelector(getCompareScenario);
  const themeColors = useSelector(getPlanPageThemeColors);
  const isKpiLoading = useSelector(selectIsKpiLoading);
  const scrollReference = useRef<HTMLDivElement | null>(null);

  const skeletonCount = useMemo(() => {
    const indicatorCount = metadata?.indicators?.length ?? 0;
    return Math.min(Math.max(indicatorCount || 4, 2), 6);
  }, [metadata?.indicators?.length]);

  const indicatorRows = isCompareMode ? compareIndicatorRows : planIndicatorRows;
  const defaultTabIndex = isCompareMode ? compareDefaultTabIndex : planDefaultTabIndex;
  const selectedIndex = isCompareMode ? compareSafeActiveIndex : planSafeActiveIndex;
  const indicatorRowsLength = indicatorRows.length;

  useEffect(() => {
    if (indicatorRowsLength === 0) return;

    const isTabUnset = activeTabIndex === -1;
    const isTabOutOfRange = activeTabIndex >= indicatorRowsLength;

    if (isTabUnset || isTabOutOfRange) {
      dispatch(setActiveTabIndexVersion2(defaultTabIndex));
    }
  }, [activeTabIndex, defaultTabIndex, dispatch, indicatorRowsLength]);

  useEffect(() => {
    if (!isCompareMode || !compareScenario?.id || indicatorRowsLength === 0) return;
    dispatch(setActiveTabIndexVersion2(defaultTabIndex));
    // Reset KPI selection when the compare scenario changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareScenario?.id]);

  const kpiEntries = useMemo(
    () =>
      indicatorRows.map((row, index) =>
        isCompareMode
          ? toCompareKpiEntry(row, varianceFilter)
          : toPlanKpiEntry(row, index, metadata),
      ),
    [indicatorRows, isCompareMode, metadata, varianceFilter],
  );

  const onSelectIndicator = useCallback(
    (index: number) => {
      dispatch(setActiveTabIndexVersion2(index));
    },
    [dispatch],
  );

  const onScrollRight = useCallback(() => {
    scrollReference.current?.scrollBy({ left: 240, behavior: 'smooth' });
  }, []);

  if (isKpiLoading) {
    return (
      <KpiSummaryBarSkeleton
        count={skeletonCount}
        borderColor={themeColors.border}
        surfaceColor={themeColors.surface}
      />
    );
  }

  if (kpiEntries.length === 0) return null;

  return (
    <Box position='relative' px={PLAN_HEADER_HORIZONTAL_PADDING} bg={themeColors.surface}>
      <Flex
        ref={scrollReference}
        alignItems='flex-end'
        gap={0}
        overflowX='auto'
        pb={0}
        css={{ '&::-webkit-scrollbar': { display: 'none' } }}
        maxWidth={`calc(100% - 50px)`}
      >
        {kpiEntries.map((entry, index) => (
          <KpiMetricCard
            key={`${entry.name}-${index}`}
            entry={entry}
            index={index}
            isSelected={index === selectedIndex}
            isCompareMode={isCompareMode}
            primaryColor={themeColors.primary}
            textDark={themeColors.textDark}
            textSecondary={themeColors.textSecondary}
            successColor={themeColors.success}
            dangerColor={themeColors.danger}
            activeBg={themeColors.activeBg}
            onSelect={onSelectIndicator}
          />
        ))}
      </Flex>

      {kpiEntries.length > 1 && (
        <>
          <Box
            position='absolute'
            right={0}
            bottom={4}
            top={0}
            w='45px'
            pointerEvents='none'
            bgGradient={`linear(to-l, ${themeColors.surface} 55%, transparent)`}
          />
          {/* <IconButton
            aria-label='Scroll metrics'
            icon={<ChevronRightIcon boxSize={8} />}
            onClick={onScrollRight}
            position='absolute'
            right={8}
            bottom='50%'
            transform='translateY(50%)'
            size='sm'
            variant='ghost'
            color={themeColors.textDark}
            bg='transparent'
            _hover={{ bg: 'transparent' }}
            zIndex={1}
          /> */}
        </>
      )}
      <Box w='100%' h='1px' bg={themeColors.border} />
      <IconButton
        aria-label='Scroll metrics'
        icon={<ChevronRightIcon boxSize={8} />}
        onClick={onScrollRight}
        position='absolute'
        right={0}
        bottom='50%'
        transform='translateY(50%)'
        size='sm'
        variant='ghost'
        color={themeColors.textDark}
        bg='transparent'
        _hover={{ bg: 'transparent' }}
        zIndex={1}
      />
    </Box>
  );
};

export default memo(KpiSummaryBar);
